// Firmware.js - Gestão de atualizações de firmware (administradores)
//
// Nem todos os fabricantes expõem firmware pela API. Os que não expõem
// aparecem na mesma na lista, com o motivo — senão a pergunta óbvia
// ("porque é que a minha TV não está aqui?") ficava sem resposta.
//
// Aplicar uma atualização é irreversível e deixa o aparelho inacessível
// durante um a dois minutos, por isso pede confirmação explícita e só
// oferece a versão estável (nunca a beta).
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, RefreshCw, ArrowUpCircle, CheckCircle2,
  AlertTriangle, WifiOff, MinusCircle
} from 'lucide-react';
import './Firmware.css';

function Firmware({ apiUrl, onBack, token }) {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(null);
  const [notice, setNotice] = useState('');

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiUrl}/firmware`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || t('firmware.erroConsultar'));
        return;
      }
      setDevices(data.devices || []);
      setStats({
        updates: data.updates_available,
        offline: data.offline,
        total: data.total,
      });
    } catch (err) {
      setError(t('firmware.erroConsultar'));
    } finally {
      setLoading(false);
    }
  }, [apiUrl, authHeaders, t]);

  useEffect(() => { load(); }, [load]);

  const applyUpdate = async (device) => {
    const target = device.available;
    const ok = window.confirm(t('firmware.confirmar', {
      nome: device.name,
      atual: device.current,
      nova: target,
    }));
    if (!ok) return;

    setUpdating(device.id);
    setNotice('');
    setError('');
    try {
      const isShelly = device.vendor === 'Shelly';
      const res = await fetch(`${apiUrl}/firmware/${isShelly ? 'shelly' : 'tuya'}/update`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(isShelly
          ? { ip: device.ip }
          : { device_id: device.device_id, module_type: device.module_type }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.detail || t('firmware.erroIniciar'));
        return;
      }
      setNotice(t('firmware.iniciada', { nome: device.name }) +
                (data.to ? ` (${data.from} → ${data.to})` : ''));
    } catch (err) {
      setError(t('firmware.erroIniciar'));
    } finally {
      setUpdating(null);
    }
  };

  // Agrupar por origem, para não ser uma lista solta de 26 linhas
  const groups = devices.reduce((acc, d) => {
    const g = d.group || 'Outros';
    (acc[g] = acc[g] || []).push(d);
    return acc;
  }, {});

  const statusOf = (d) => {
    if (!d.supported) return { icon: MinusCircle, cls: 'na', text: d.reason };
    if (d.online === false) return { icon: WifiOff, cls: 'offline', text: t('firmware.inacessivel') };
    if (d.upgrading) return { icon: RefreshCw, cls: 'upgrading', text: t('firmware.aAtualizar') };
    if (d.update_available) return { icon: ArrowUpCircle, cls: 'update', text: t('firmware.disponivel', { versao: d.available }) };
    return { icon: CheckCircle2, cls: 'ok', text: t('firmware.atualizado') };
  };

  return (
    <div className="fw-view">
      <div className="fw-header">
        <button onClick={onBack} className="fw-back"><ArrowLeft size={22} /></button>
        <div>
          <h1><ArrowUpCircle size={26} /> {t('firmware.titulo')}</h1>
          <p>{t('firmware.subtitulo')}</p>
        </div>
        <button onClick={load} className="fw-refresh" disabled={loading}>
          <RefreshCw size={18} className={loading ? 'spinning' : ''} />
        </button>
      </div>

      {stats && (
        <div className="fw-summary">
          <div className={`fw-stat ${stats.updates ? 'update' : ''}`}>
            <ArrowUpCircle size={18} />
            <div><strong>{stats.updates}</strong><span>{t('firmware.porAtualizar')}</span></div>
          </div>
          <div className={`fw-stat ${stats.offline ? 'offline' : ''}`}>
            <WifiOff size={18} />
            <div><strong>{stats.offline}</strong><span>{t('firmware.inacessiveis')}</span></div>
          </div>
          <div className="fw-stat">
            <CheckCircle2 size={18} />
            <div><strong>{stats.total}</strong><span>{t('firmware.aparelhos')}</span></div>
          </div>
        </div>
      )}

      {notice && <div className="fw-notice"><CheckCircle2 size={18} /> {notice}</div>}
      {error && <div className="fw-error"><AlertTriangle size={18} /> {error}</div>}

      {loading ? (
        <div className="fw-empty">
          <RefreshCw size={32} className="spinning" />
          <p>{t('firmware.aConsultar')}</p>
          <span>{t('firmware.aConsultarDica')}</span>
        </div>
      ) : (
        Object.entries(groups).map(([group, items]) => (
          <div className="fw-group" key={group}>
            <h3>{group}</h3>
            <div className="fw-list">
              {items.map(d => {
                const st = statusOf(d);
                const Icon = st.icon;
                return (
                  <div className={`fw-row ${st.cls}`} key={d.id}>
                    <div className="fw-row-main">
                      <span className="fw-name">{d.name}</span>
                      <span className="fw-vendor">{d.vendor}{d.model ? ` · ${d.model}` : ''}</span>
                    </div>

                    <span className="fw-version">
                      {d.current ? `v${d.current}` : '—'}
                    </span>

                    <span className={`fw-status ${st.cls}`}>
                      <Icon size={15} className={d.upgrading ? 'spinning' : ''} />
                      {st.text}
                    </span>

                    {d.update_available ? (
                      <button
                        className="fw-btn"
                        onClick={() => applyUpdate(d)}
                        disabled={updating === d.id}
                      >
                        {updating === d.id ? t('firmware.aIniciar') : t('firmware.atualizar')}
                      </button>
                    ) : (
                      <span className="fw-btn-placeholder">
                        {d.beta ? `beta ${d.beta}` : ''}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      <p className="fw-footnote">
        {t('firmware.rodape')}
      </p>
    </div>
  );
}

export default Firmware;
